import { AlertCircle, CheckCircle2, X } from "lucide-react";
import { useEffect } from "react";

type ToastType = "success" | "error";

interface ToastProps {
	message: string;
	type?: ToastType;
	visible: boolean;
	onClose: () => void;
	duration?: number;
	icon?: boolean;
}

/**
 * Toast-Komponente für Benachrichtigungen
 *
 * @param message - Die anzuzeigende Nachricht
 * @param type - Typ des Toasts (success oder error)
 * @param visible - Ob der Toast angezeigt werden soll
 * @param onClose - Funktion zum Schließen des Toasts
 * @param duration - Dauer in Millisekunden, nach der der Toast automatisch geschlossen wird
 * @param icon - Ob ein Icon angezeigt werden soll
 */
export default function Toast({
	message,
	type = "success",
	visible,
	onClose,
	duration = 3000,
	icon = true,
}: ToastProps) {
	// Automatisches Schließen nach duration
	useEffect(() => {
		if (visible) {
			const timer = setTimeout(() => {
				onClose();
			}, duration);

			return () => clearTimeout(timer);
		}
	}, [visible, onClose, duration]);

	if (!visible) return null;

	const styles = {
		success:
			"bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300",
		error:
			"bg-rose-50 dark:bg-rose-900/30 border-rose-200 dark:border-rose-700 text-rose-700 dark:text-rose-300",
	};

	const icons = {
		success: <CheckCircle2 className="mr-2 h-5 w-5" />,
		error: <AlertCircle className="mr-2 h-5 w-5" />,
	};

	return (
		<div
			className={`fade-in slide-in-from-bottom-5 fixed right-4 bottom-4 z-50 flex animate-in items-center gap-2 rounded-md border p-3 shadow-lg transition-all duration-500 ${styles[type]}`}
		>
			{icon && icons[type]}
			<span>{message}</span>
			<button
				className="ml-2 rounded-full p-1 hover:bg-black/5 dark:hover:bg-white/10"
				onClick={onClose}
			>
				<X className="h-4 w-4" />
			</button>
		</div>
	);
}
